import { createContext, use, useEffect, useSyncExternalStore } from 'react';

type ThemeValue = 'light' | 'dark' | null;

function getThemeSnapshot(): ThemeValue {
  const value = getComputedStyle(document.body).getPropertyValue('--starthub-academy-theme').trim();
  return value === 'light' || value === 'dark' ? value : null;
}

function subscribeToThemeChanges(onStoreChange: () => void): () => void {
  const media = window.matchMedia('(prefers-color-scheme: light)');
  media.addEventListener('change', onStoreChange);
  return () => media.removeEventListener('change', onStoreChange);
}

function setMetaColorScheme(theme: ThemeValue): void {
  if (!theme) return;
  const meta = document.querySelector('meta[name="color-scheme"]');
  if (meta) {
    meta.setAttribute('content', theme);
    return;
  }
  const newMeta = document.createElement('meta');
  newMeta.name = 'color-scheme';
  newMeta.content = theme;
  document.head.appendChild(newMeta);
}

// A hook meant to be used exactly once at the root of the app. Handles setting
// the theme based on the user's configuration — light, dark, or system
// default.
//
// Returns the theme currently being used — either `light` or `dark`, or `null`
// if the value has not yet been determined.
export function useThemeRoot(): ThemeValue {
  const currentTheme = useSyncExternalStore(subscribeToThemeChanges, getThemeSnapshot, () => null);
  useEffect(() => {
    setMetaColorScheme(currentTheme);
  }, [currentTheme]);

  return currentTheme;
}

// A hook that can be used anywhere in the app. Returns the name of the theme
// currently being used — either `light` or `dark`.
//
// Please use this sparingly! The purpose of theming is that we can define a
// set list of color names, then use those names without needing to know which
// theme is active. If there are major differences between light and dark
// themes that require awareness of the current theme in your own CSS, that's
// generally a problem that should be fixed on the design side.
export function useTheme(): ThemeValue {
  return use(ThemeContext);
}

export const ThemeContext = createContext<ThemeValue>(null);
export const ThemeContextProvider = ThemeContext.Provider;
